import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ENV, Environment } from 'lib-trend-core';
import { CHAT_APP_OPEN_ACK, CHAT_APP_OPEN_MESSAGE, CHAT_APP_WINDOW_NAME, isSafeChatAppPath } from 'lib-trend-core/chat';
import { ChatAppSessionService } from './chat-app-session.service';

interface ChatAppOpenMessage {
  type: string;
  requestId: string;
  path: string;
  code?: string;
}

/**
 * The chat app's side of the opener protocol — SPEC-2026-09-internal-chat-pwa (T-40 ·
 * RF-11 · RF-12).
 *
 * When the product maximizes a conversation and a chat app window is already open, it
 * posts the destination (and a fresh one-time code) instead of reloading that window:
 * the conversation opens in place, drafts and scroll intact, and the window is focused.
 *
 * Only messages from the product's own origin are honoured, and only paths of the chat
 * app's URL contract are followed. A code for a different person or company switches the
 * session exactly like the handoff landing does (RF-07).
 */
@Injectable({ providedIn: 'root' })
export class ChatAppOpenerService {

  private started = false;

  private readonly handledRequests = new Set<string>();

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly session: ChatAppSessionService,
    @Inject(ENV) private readonly environment: Environment,
  ) { }

  start(): void {
    if (this.started) return;
    this.started = true;

    // Lets the product find a chat app window the user opened on their own.
    try {
      window.name = CHAT_APP_WINDOW_NAME;
    } catch {
      /* not settable in this context */
    }

    window.addEventListener('message', (event: MessageEvent) => this.onMessage(event));
  }

  private onMessage(event: MessageEvent): void {
    const productOrigin = this.productOrigin();
    if (!productOrigin || event.origin !== productOrigin) return;

    const data = event.data as ChatAppOpenMessage;
    if (data?.type !== CHAT_APP_OPEN_MESSAGE || typeof data.requestId !== 'string') return;

    // Acknowledge every copy (the product retries until acknowledged), act on one.
    (event.source as WindowProxy | null)?.postMessage({ type: CHAT_APP_OPEN_ACK, requestId: data.requestId }, productOrigin);
    if (this.handledRequests.has(data.requestId)) return;
    this.handledRequests.add(data.requestId);

    const path = isSafeChatAppPath(data.path) ? data.path : '/';
    this.zone.run(() => this.open(path, typeof data.code === 'string' ? data.code : null));
  }

  private async open(path: string, code: string | null): Promise<void> {
    window.focus();

    if (code) {
      try {
        const exchanged = await this.session.exchangeHandoffCode(code);
        if (!this.session.isSameIdentity(exchanged)) {
          await this.session.adopt(exchanged);
          // Another person: nothing of the previous session may stay on screen.
          window.location.assign(path);
          return;
        }
      } catch {
        // Used, expired or refused code: the current session simply stays.
      }
    }

    await this.router.navigateByUrl(path);
  }

  private productOrigin(): string | null {
    try {
      return new URL(String(this.environment['trendWebAppUrl'])).origin;
    } catch {
      return null;
    }
  }
}
